import { useState, useEffect, useRef } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { useAdminViewAs } from '@/contexts/AdminViewAsContext';
import { safeError } from '@/lib/safeError';

type ElevatedRole = 'admin' | 'superadmin' | 'moderator';

interface UseIsAdminOrModeratorReturn {
  /** Has any elevated role (admin, superadmin, or moderator) */
  isAdminOrMod: boolean;
  /** True only for admin/superadmin — gates admin-only features */
  isAdmin: boolean;
  /** True only for moderator role */
  isModerator: boolean;
  /** The actual role string, or null for regular users */
  role: ElevatedRole | null;
  loading: boolean;
}

// Module-level cache: one DB query per user session, shared across all mounts
const roleCache = new Map<string, { role: ElevatedRole | null; ts: number }>();
const CACHE_TTL = 5 * 60 * 1000; // 5 minutes

export function useIsAdminOrModerator(): UseIsAdminOrModeratorReturn {
  const { user } = useAuth();
  const [role, setRole] = useState<ElevatedRole | null>(null);
  const [loading, setLoading] = useState(true);
  const fetchedRef = useRef<string | null>(null);

  useEffect(() => {
    const checkRole = async () => {
      if (!user) {
        setRole(null);
        setLoading(false);
        return;
      }

      // Return cached result if fresh
      const cached = roleCache.get(user.id);
      if (cached && Date.now() - cached.ts < CACHE_TTL) {
        setRole(cached.role);
        setLoading(false);
        fetchedRef.current = user.id;
        return;
      }

      // Prevent duplicate fetches for the same user within this mount
      if (fetchedRef.current === user.id) return;
      fetchedRef.current = user.id;

      try {
        const { data, error } = await supabase
          .from('user_roles')
          .select('role')
          .eq('user_id', user.id)
          .in('role', ['admin', 'superadmin', 'moderator'])
          .maybeSingle();

        if (error) throw error;
        const resolved = (data?.role as ElevatedRole) ?? null;
        setRole(resolved);
        roleCache.set(user.id, { role: resolved, ts: Date.now() });
      } catch (error) {
        console.error('Error checking role:', safeError(error));
        setRole(null);
      } finally {
        setLoading(false);
      }
    };

    checkRole();
  }, [user]);

  // Admin "View As" override
  const { overrides } = useAdminViewAs();
  if (overrides?.hideAdminUI) {
    return {
      isAdminOrMod: false,
      isAdmin: false,
      isModerator: false,
      role: null,
      loading,
    };
  }

  const isAdmin = role === 'admin' || role === 'superadmin';
  const isModerator = role === 'moderator';

  return {
    isAdminOrMod: isAdmin || isModerator,
    isAdmin,
    isModerator,
    role,
    loading,
  };
}
